'use client';

import { type ReactNode, useState } from 'react';

import { Trans } from '@kit/ui/trans';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import {
  EmptyState,
  Eyebrow,
  HairlineDivider,
  NativePageShell,
} from '~/(tenant)/_components/native/_primitives';
import { VenueBar } from '~/(tenant)/_components/venue-picker/venue-bar';
import type { PublicDiscountCode } from '~/(tenant)/book/_lib/public-discount-codes';

import type { AvailableMembershipPlan } from '../_lib/server/available-memberships.loader';
import type { MyMembershipRow } from '../_lib/server/memberships-page.loader';
import { NativeMembershipCard } from './native-membership-card';
import { MembershipRateCards } from './membership-rate-cards';
import { SelfServeCatalog } from './self-serve-membership-catalog';

type Tab = 'active' | 'purchase';

interface NativeMembershipsPageProps {
  memberships: MyMembershipRow[];
  availablePlans: AvailableMembershipPlan[];
  isAuthenticated: boolean;
  wallet: { enabled: boolean; balance: number };
  tenantId: string;
  publicDiscountCodes: PublicDiscountCode[];
}

/**
 * Memberships landing. Public when no user is signed in (renders only the
 * purchase tab); for authenticated users it surfaces their existing
 * memberships alongside the catalog. Mirrors the /packages tab structure so
 * the two surfaces feel like one product.
 */
export function NativeMembershipsPage({
  memberships,
  availablePlans,
  isAuthenticated,
  wallet,
  tenantId,
  publicDiscountCodes,
}: NativeMembershipsPageProps) {
  const showActiveTab = isAuthenticated;
  const hasMemberships = memberships.length > 0;

  // Anonymous users only see purchase. Authenticated users default to active
  // when they have memberships; otherwise they land on purchase (matches the
  // ergonomics of the /packages page for first-time customers).
  const initialTab: Tab =
    showActiveTab && hasMemberships ? 'active' : 'purchase';
  const [tab, setTab] = useState<Tab>(initialTab);

  return (
    <NativePageShell
      eyebrow={
        showActiveTab ? (
          <Trans
            i18nKey="members:nativeView.eyebrow"
            defaults="Membership · Plans"
          />
        ) : (
          <Trans
            i18nKey="members:nativeView.publicEyebrow"
            defaults="Membership · Join"
          />
        )
      }
      title={
        showActiveTab ? (
          <Trans i18nKey="common:myMemberships" defaults="My memberships" />
        ) : (
          <Trans
            i18nKey="members:nativeView.publicTitle"
            defaults="Become a member"
          />
        )
      }
    >
      {showActiveTab ? (
        <SegmentedTabs
          tab={tab}
          onChange={setTab}
          activeCount={memberships.length}
          availableCount={availablePlans.length}
        />
      ) : null}

      {tab === 'active' && showActiveTab ? (
        <ActiveTab
          memberships={memberships}
          hasAvailable={availablePlans.length > 0}
          onSwitchToPurchase={() => setTab('purchase')}
        />
      ) : null}

      {tab === 'purchase' ? <VenueBar className="mb-5" /> : null}
      {tab === 'purchase' ? (
        <PurchaseTab
          plans={availablePlans}
          isAuthenticated={isAuthenticated}
          wallet={wallet}
          tenantId={tenantId}
          publicDiscountCodes={publicDiscountCodes}
        />
      ) : null}
    </NativePageShell>
  );
}

/* -------------------------------------------------------------------------- */
/* Tabs                                                                       */
/* -------------------------------------------------------------------------- */

function SegmentedTabs({
  tab,
  onChange,
  activeCount,
  availableCount,
}: {
  tab: Tab;
  onChange: (next: Tab) => void;
  activeCount: number;
  availableCount: number;
}) {
  const t = useNativeTokens();
  return (
    <div
      role="tablist"
      style={{
        display: 'flex',
        gap: 24,
        flexWrap: 'wrap',
        borderBottom: `1px solid ${t.line}`,
        marginBottom: 20,
      }}
    >
      <SerifTab
        active={tab === 'active'}
        onClick={() => onChange('active')}
        count={activeCount}
      >
        <Trans
          i18nKey="common:memberClub.memberships.tabs.active"
          defaults="Active"
        />
      </SerifTab>
      <SerifTab
        active={tab === 'purchase'}
        onClick={() => onChange('purchase')}
        count={availableCount}
      >
        <Trans
          i18nKey="common:memberClub.memberships.tabs.purchase"
          defaults="Purchase"
        />
      </SerifTab>
    </div>
  );
}

function SerifTab({
  active,
  onClick,
  children,
  count,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  count: number;
}) {
  const t = useNativeTokens();
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '6px 0 12px',
        position: 'relative',
        color: active ? t.foreground : t.muted,
        display: 'flex',
        alignItems: 'baseline',
        gap: 8,
        marginBottom: -1,
        borderRadius: 0,
        borderBottom: active
          ? `2px solid ${t.foreground}`
          : '2px solid transparent',
      }}
    >
      <span
        style={{
          fontFamily: t.fontDisplay,
          fontSize: 22,
          fontWeight: active ? 500 : 400,
          letterSpacing: '-0.01em',
        }}
      >
        {children}
      </span>
      <span
        style={{
          fontFamily: t.fontMono,
          fontSize: 9,
          color: t.muted,
        }}
      >
        {count}
      </span>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* Active tab                                                                 */
/* -------------------------------------------------------------------------- */

function ActiveTab({
  memberships,
  hasAvailable,
  onSwitchToPurchase,
}: {
  memberships: MyMembershipRow[];
  hasAvailable: boolean;
  onSwitchToPurchase: () => void;
}) {
  const t = useNativeTokens();

  if (memberships.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 32px' }}>
        <Eyebrow style={{ marginBottom: 14 }}>
          <Trans
            i18nKey="common:memberClub.memberships.emptyState.eyebrow"
            defaults="No membership yet"
          />
        </Eyebrow>
        <div
          style={{
            fontFamily: t.fontDisplay,
            fontSize: 28,
            fontWeight: 300,
            color: t.foreground,
            lineHeight: 1.15,
            letterSpacing: '-0.01em',
          }}
        >
          <Trans
            i18nKey="common:memberClub.memberships.emptyState.title"
            defaults="Your card"
          />
          <br />
          <em style={{ fontStyle: 'italic' }}>
            <Trans
              i18nKey="common:memberClub.memberships.emptyState.emphasis"
              defaults="awaits."
            />
          </em>
        </div>
        <p
          style={{
            fontFamily: t.fontSans,
            fontSize: 13,
            color: t.muted,
            marginTop: 16,
            lineHeight: 1.55,
            maxWidth: 280,
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <Trans
            i18nKey="common:memberClub.memberships.emptyState.body"
            defaults="Memberships unlock easier check-in, guest invites, and member-only pricing."
          />
        </p>
        {hasAvailable ? (
          <button
            type="button"
            onClick={onSwitchToPurchase}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              marginTop: 28,
              padding: '14px 28px',
              backgroundColor: t.accent,
              color: t.background,
              borderRadius: t.radius,
              border: 'none',
              cursor: 'pointer',
              fontFamily: t.fontSans,
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}
          >
            <Trans
              i18nKey="common:memberClub.memberships.emptyState.cta"
              defaults="See plans"
            />
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      {memberships.map((membership, index) => (
        <div key={membership.id}>
          {index > 0 ? <HairlineDivider /> : null}
          <NativeMembershipCard membership={membership} />
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Purchase tab                                                               */
/* -------------------------------------------------------------------------- */

function PurchaseTab({
  plans,
  isAuthenticated,
  wallet,
  tenantId,
  publicDiscountCodes,
}: {
  plans: AvailableMembershipPlan[];
  isAuthenticated: boolean;
  wallet: { enabled: boolean; balance: number };
  tenantId: string;
  publicDiscountCodes: PublicDiscountCode[];
}) {
  const t = useNativeTokens();

  // Self-serve checkout (paid plans → dialog; free → instant). Approval plans
  // keep linking to the /membership intake form (no onPurchase handler passed).
  const hasSelfServe = plans.some((p) => p.requires_approval === false);

  if (plans.length === 0) {
    return (
      <EmptyState
        mark="quiet"
        eyebrow={
          <Trans
            i18nKey="common:memberClub.memberships.purchase.emptyEyebrow"
            defaults="Catalog at rest"
          />
        }
        title={
          <Trans
            i18nKey="common:memberClub.memberships.purchase.emptyTitle"
            defaults="No plans"
          />
        }
        emphasis={
          <Trans
            i18nKey="common:memberClub.memberships.purchase.emptyEmphasis"
            defaults="available."
          />
        }
        body={
          <Trans
            i18nKey="common:memberClub.memberships.purchase.emptyBody"
            defaults="Check back soon — new memberships are released regularly."
          />
        }
      />
    );
  }

  return (
    <div>
      <Eyebrow size="md" style={{ marginBottom: 14 }}>
        <Trans
          i18nKey="common:memberClub.memberships.purchase.heading"
          defaults="For purchase"
        />
      </Eyebrow>

      {hasSelfServe ? (
        <SelfServeCatalog
          plans={plans}
          isAuthenticated={isAuthenticated}
          wallet={wallet}
          tenantId={tenantId}
          publicDiscountCodes={publicDiscountCodes}
        />
      ) : (
        <MembershipRateCards plans={plans} />
      )}

      <p
        style={{
          marginTop: 16,
          paddingTop: 20,
          paddingBottom: 12,
          borderTop: `1px solid ${t.line}`,
          fontFamily: t.fontSans,
          fontSize: 11,
          color: t.muted,
          lineHeight: 1.6,
          textAlign: 'center',
        }}
      >
        <Trans
          i18nKey="common:memberClub.memberships.purchase.finePrint"
          defaults="Pricing and benefits set by the venue. Some plans require admin approval."
        />
      </p>
    </div>
  );
}
